import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Sparkles, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BulkUploadModal = ({ open, onOpenChange, onComplete }) => {
  const [bulkImages, setBulkImages] = useState([]);
  const [bulkFormData, setBulkFormData] = useState([]);
  const [bulkAnalyzing, setBulkAnalyzing] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleImagesSelect = (e) => {
    const files = Array.from(e.target.files);
    setBulkImages(files);
    const initialFormData = files.map((_, index) => ({
      title: `Artwork ${index + 1}`,
      description: '',
      year: new Date().getFullYear().toString(),
      medium: 'Oil on Canvas',
      dimensions: '36 x 36 inches',
      category: 'Uncategorized',
      art_type: 'Traditional',
      featured: false,
      price: '',
      for_sale: true,
      tags: ''
    }));
    setBulkFormData(initialFormData);
  };

  // Always update from the LATEST state (functional update). Copying `bulkFormData` from the render
  // closure made each AI result overwrite earlier results and edits while other images were analysed.
  const handleFormChange = (index, field, value) => {
    setBulkFormData(prev => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  const handleAiAnalyze = async (file, onResult) => {
    const token = localStorage.getItem('admin_token');
    const fd = new FormData();
    fd.append('image', file);
    try {
      const res = await axios.post(`${API}/artworks/analyze-image`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
      if (res.data.success) {
        onResult(res.data);
        toast.success('AI generated title & description!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI analysis failed');
    }
  };

  const handleAnalyzeSingle = async (index) => {
    const file = bulkImages[index];
    if (!file) return;
    setBulkAnalyzing(prev => ({ ...prev, [index]: true }));
    await handleAiAnalyze(file, (data) => {
      // Only this image's entry changes; every other image keeps its current (possibly edited) values.
      setBulkFormData(prev => prev.map((item, i) => (i !== index ? item : {
        ...item,
        title: data.title || item.title,
        description: data.description || item.description,
        medium: data.medium || item.medium,
        category: data.category || item.category,
        tags: (data.tags || []).join(', ') || item.tags || ''
      })));
    });
    setBulkAnalyzing(prev => ({ ...prev, [index]: false }));
  };

  const handleAnalyzeAll = async () => {
    for (let i = 0; i < bulkImages.length; i++) {
      await handleAnalyzeSingle(i);
    }
    toast.success('All images analyzed!');
  };

  const handleUpload = async () => {
    if (bulkImages.length === 0) { toast.error('Please select images'); return; }
    setIsSubmitting(true);
    const token = localStorage.getItem('admin_token');
    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < bulkImages.length; i++) {
      const fd = new FormData();
      const data = bulkFormData[i];
      Object.keys(data).forEach(key => {
        if (key === 'tags') {
          const tagsStr = data[key];
          const tagsArr = tagsStr ? tagsStr.split(',').map(t => t.trim().replace(/^#/, '')).filter(Boolean) : [];
          fd.append('tags', JSON.stringify(tagsArr));
        } else if (data[key] !== null && data[key] !== '') {
          fd.append(key, data[key]);
        }
      });
      fd.append('image', bulkImages[i]);
      try {
        await axios.post(`${API}/artworks`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        successCount++;
      } catch {
        failCount++;
      }
    }

    setIsSubmitting(false);
    if (successCount > 0) toast.success(`Uploaded ${successCount} artwork(s)`);
    if (failCount > 0) toast.error(`Failed to upload ${failCount} artwork(s)`);
    onOpenChange(false);
    setBulkImages([]);
    setBulkFormData([]);
    if (onComplete) onComplete();
  };

  const handleClose = () => {
    onOpenChange(false);
    setBulkImages([]);
    setBulkFormData([]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="bulk-upload-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-light">Bulk Upload Artworks</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <div>
            <Label htmlFor="bulk-images">Select Multiple Images *</Label>
            <Input id="bulk-images" type="file" accept="image/*" multiple onChange={handleImagesSelect} data-testid="bulk-images-input" className="mt-2" />
            <p className="text-sm text-muted-foreground mt-2">
              {bulkImages.length > 0 ? `${bulkImages.length} image(s) selected` : 'No images selected'}
            </p>
          </div>

          {bulkImages.length > 0 && (
            <div className="space-y-8">
              <div className="flex justify-between items-center">
                <h3 className="font-sans text-lg font-medium">Edit Details for Each Artwork</h3>
                <Button type="button" onClick={handleAnalyzeAll} disabled={Object.values(bulkAnalyzing).some(Boolean)}
                  className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="bulk-ai-analyze-all-btn">
                  <Sparkles size={14} className="mr-2" /> AI Analyze All
                </Button>
              </div>

              {bulkImages.map((file, index) => (
                <div key={index} className="border border-border rounded-lg p-6 space-y-4">
                  <div className="flex items-start gap-6">
                    <div className="w-32 h-32 flex-shrink-0">
                      <img src={URL.createObjectURL(file)} alt={`Preview ${index + 1}`} className="w-full h-full object-cover rounded" />
                    </div>
                    <div className="flex-1 space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-sans font-medium text-lg">Artwork {index + 1}</h4>
                        <Button type="button" size="sm" onClick={() => handleAnalyzeSingle(index)} disabled={bulkAnalyzing[index]}
                          className="bg-violet-600 hover:bg-violet-700 text-white text-xs" data-testid={`bulk-ai-analyze-${index}`}>
                          {bulkAnalyzing[index] ? <><Loader2 size={12} className="mr-1 animate-spin" /> Analyzing...</> : <><Sparkles size={12} className="mr-1" /> AI Generate</>}
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Title *</Label>
                          <Input value={bulkFormData[index]?.title || ''} onChange={(e) => handleFormChange(index, 'title', e.target.value)} placeholder="Enter title" />
                        </div>
                        <div>
                          <Label>Year</Label>
                          <Input value={bulkFormData[index]?.year || ''} onChange={(e) => handleFormChange(index, 'year', e.target.value)} placeholder="2024" />
                        </div>
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea value={bulkFormData[index]?.description || ''} onChange={(e) => handleFormChange(index, 'description', e.target.value)} placeholder="Description" rows={3} />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>Medium</Label>
                          <Input value={bulkFormData[index]?.medium || ''} onChange={(e) => handleFormChange(index, 'medium', e.target.value)} placeholder="Oil on Canvas" />
                        </div>
                        <div>
                          <Label>Dimensions</Label>
                          <Input value={bulkFormData[index]?.dimensions || ''} onChange={(e) => handleFormChange(index, 'dimensions', e.target.value)} placeholder="36 x 36 inches" />
                        </div>
                        <div>
                          <Label>Category</Label>
                          <Input value={bulkFormData[index]?.category || ''} onChange={(e) => handleFormChange(index, 'category', e.target.value)} placeholder="Mythology" />
                        </div>
                      </div>
                      <div>
                        <Label>Art Type</Label>
                        <select value={bulkFormData[index]?.art_type || 'Traditional'} onChange={(e) => handleFormChange(index, 'art_type', e.target.value)}
                          className="w-full px-4 py-2 bg-background border border-border rounded-lg font-sans text-base focus:outline-none focus:ring-2 focus:ring-accent">
                          <option value="Traditional">Traditional (Oil, Canvas, etc.)</option>
                          <option value="Digital">Digital Art</option>
                        </select>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Price (INR)</Label>
                          <Input type="number" min="0" step="100" value={bulkFormData[index]?.price || ''}
                            onChange={(e) => handleFormChange(index, 'price', e.target.value)}
                            placeholder={bulkFormData[index]?.art_type === 'Digital' ? '5000' : '10000'} />
                        </div>
                        <div className="flex items-end">
                          <div className="flex items-center space-x-2 pb-2">
                            <Checkbox id={`for-sale-${index}`} checked={bulkFormData[index]?.for_sale !== false}
                              onCheckedChange={(checked) => handleFormChange(index, 'for_sale', checked)} />
                            <Label htmlFor={`for-sale-${index}`} className="cursor-pointer">For sale</Label>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox id={`featured-${index}`} checked={bulkFormData[index]?.featured || false}
                          onCheckedChange={(checked) => handleFormChange(index, 'featured', checked)} />
                        <Label htmlFor={`featured-${index}`} className="cursor-pointer">Featured artwork</Label>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-4 pt-4 border-t">
            <Button type="button" variant="outline" onClick={handleClose} data-testid="bulk-cancel-button">Cancel</Button>
            <Button onClick={handleUpload} disabled={isSubmitting || bulkImages.length === 0} className="bg-accent hover:bg-accent/90" data-testid="bulk-upload-submit-button">
              {isSubmitting ? `Uploading... (${bulkImages.length} images)` : `Upload ${bulkImages.length} Artwork(s)`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkUploadModal;
